import { useState, useEffect } from 'react';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { 
  PenLine, Sparkles, Plus, Calendar as CalendarIcon, 
  ChevronLeft, Loader2, Smile, Meh, Frown, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_OPTIONS = [
  { value: 1, icon: Frown, label: 'Struggling', color: 'text-red-500' },
  { value: 2, icon: Frown, label: 'Low', color: 'text-orange-500' },
  { value: 3, icon: Meh, label: 'Okay', color: 'text-yellow-500' },
  { value: 4, icon: Smile, label: 'Good', color: 'text-lime-500' },
  { value: 5, icon: Smile, label: 'Great', color: 'text-emerald-500' },
];

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, new, detail, calendar
  const [newEntry, setNewEntry] = useState({ text: '', mood_before: null });
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reflecting, setReflecting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [entriesRes, promptRes] = await Promise.all([
        axios.get(`${API}/journal`),
        axios.get(`${API}/journal/prompt/today`)
      ]);
      setEntries(entriesRes.data);
      setPrompt(promptRes.data.prompt);
    } catch (error) {
      console.error('Error fetching journal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createEntry = async () => {
    if (!newEntry.text.trim()) {
      toast.error('Please write something first');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/journal`, newEntry);
      setEntries(prev => [response.data, ...prev]);
      setNewEntry({ text: '', mood_before: null });
      setView('list');
      toast.success('Entry saved');
    } catch (error) {
      toast.error('Could not save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const getReflection = async (entryId) => {
    setReflecting(true);
    try {
      const response = await axios.post(`${API}/journal/${entryId}/reflect`);
      setSelectedEntry(prev => ({ ...prev, ai_reflection: response.data.reflection }));
      setEntries(prev => 
        prev.map(e => e.id === entryId ? { ...e, ai_reflection: response.data.reflection } : e)
      );
      toast.success('Reflection ready');
    } catch (error) {
      toast.error('Could not get reflection');
    } finally {
      setReflecting(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axios.delete(`${API}/journal/${entryId}`);
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setSelectedEntry(null);
      setView('list');
      toast.success('Entry deleted');
    } catch (error) {
      toast.error('Could not delete entry');
    }
  };

  const entriesWithDates = entries.reduce((acc, entry) => {
    const date = format(parseISO(entry.created_at), 'yyyy-MM-dd');
    acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});

  const renderView = () => {
    switch (view) {
      case 'new':
        return (
          <div className="space-y-6 animate-fade-in" data-testid="new-entry-view">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setView('list')}
              className="rounded-full"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <div>
              <h2 className="text-xl font-serif mb-2">New Entry</h2>
              <p className="text-sm text-muted-foreground">{prompt}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">How are you feeling?</label>
                <div className="flex gap-2">
                  {MOOD_OPTIONS.map((mood) => {
                    const Icon = mood.icon;
                    return (
                      <button
                        key={mood.value}
                        onClick={() => setNewEntry(prev => ({ ...prev, mood_before: mood.value }))}
                        className={`flex-1 p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                          newEntry.mood_before === mood.value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/30'
                        }`}
                        data-testid={`mood-${mood.value}`}
                      >
                        <Icon className={`w-6 h-6 ${mood.color}`} />
                        <span className="text-xs">{mood.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Textarea
                placeholder="Write your thoughts..."
                value={newEntry.text}
                onChange={(e) => setNewEntry(prev => ({ ...prev, text: e.target.value }))}
                className="min-h-[200px] rounded-2xl bg-muted/50 border-transparent resize-none font-handwriting text-lg"
                data-testid="journal-textarea"
              />

              <Button
                onClick={createEntry}
                disabled={!newEntry.text.trim() || submitting}
                className="w-full h-12 rounded-full"
                data-testid="save-entry-btn"
              >
                {submitting ? 'Saving...' : 'Save Entry'}
              </Button>
            </div>
          </div>
        );

      case 'detail':
        if (!selectedEntry) return null;
        return (
          <div className="space-y-6 animate-fade-in" data-testid="entry-detail-view">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedEntry(null);
                  setView('list');
                }}
                className="rounded-full"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => deleteEntry(selectedEntry.id)}
                className="rounded-full text-destructive hover:bg-destructive/10"
                data-testid="delete-entry-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">
                {format(parseISO(selectedEntry.created_at), 'EEEE, MMMM d, yyyy • h:mm a')}
              </p>
              {selectedEntry.mood_before && (
                <div className="flex items-center gap-2 mt-2">
                  {(() => {
                    const mood = MOOD_OPTIONS.find(m => m.value === selectedEntry.mood_before);
                    const Icon = mood?.icon || Meh;
                    return (
                      <>
                        <Icon className={`w-5 h-5 ${mood?.color || ''}`} />
                        <span className="text-sm">{mood?.label}</span>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <Card className="rounded-2xl border-border/40">
              <CardContent className="p-6">
                <p className="text-foreground font-handwriting text-lg leading-relaxed whitespace-pre-wrap">
                  {selectedEntry.text}
                </p>
              </CardContent>
            </Card>

            {selectedEntry.ai_reflection ? (
              <Card className="rounded-2xl bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <span className="font-medium text-primary">AI Reflection</span>
                  </div>
                  <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                    {selectedEntry.ai_reflection}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Button
                variant="outline"
                onClick={() => getReflection(selectedEntry.id)}
                disabled={reflecting}
                className="w-full rounded-full"
                data-testid="get-reflection-btn"
              >
                {reflecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Getting reflection...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Get AI Reflection
                  </>
                )}
              </Button>
            )}
          </div>
        );

      case 'calendar':
        return (
          <div className="space-y-4 animate-fade-in" data-testid="calendar-view">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setView('list')}
              className="rounded-full"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  setSelectedDate(date);
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const entry = entries.find(e => 
                    format(parseISO(e.created_at), 'yyyy-MM-dd') === dateStr
                  );
                  if (entry) {
                    setSelectedEntry(entry);
                    setView('detail');
                  }
                }
              }}
              modifiers={{
                hasEntry: (date) => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  return !!entriesWithDates[dateStr];
                }
              }}
              modifiersStyles={{
                hasEntry: { backgroundColor: 'hsl(var(--primary) / 0.2)' }
              }}
              className="rounded-2xl border"
            />
          </div>
        );

      default:
        return (
          <div className="space-y-4 pb-24" data-testid="entries-list">
            {entries.length === 0 ? (
              <div className="text-center py-12">
                <PenLine className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-2">No journal entries yet</p>
                <p className="text-sm text-muted-foreground">Start your first entry</p>
              </div>
            ) : (
              entries.map((entry) => (
                <Card
                  key={entry.id}
                  className="rounded-2xl border-border/40 cursor-pointer hover:shadow-float transition-all"
                  onClick={() => {
                    setSelectedEntry(entry);
                    setView('detail');
                  }}
                  data-testid={`entry-${entry.id}`}
                >
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground mb-2">
                      {format(parseISO(entry.created_at), 'EEEE, MMM d')}
                    </p>
                    <p className="text-foreground line-clamp-3 font-handwriting">
                      {entry.text}
                    </p>
                    {entry.ai_reflection && (
                      <div className="flex items-center gap-1 mt-2 text-primary">
                        <Sparkles className="w-3 h-3" />
                        <span className="text-xs">Has reflection</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="journal-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-serif">Journal</h1>
          {view === 'list' && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={() => setView('calendar')}
                data-testid="calendar-btn"
              >
                <CalendarIcon className="w-5 h-5" />
              </Button>
              <Button
                size="icon"
                className="rounded-full"
                onClick={() => setView('new')}
                data-testid="new-entry-btn"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <ScrollArea className="flex-1 px-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          renderView()
        )}
      </ScrollArea>

      <BottomNav />
    </div>
  );
}
